import {
  AloftTime,
  MainPropertyType,
  ORDER_DEPRECIATION_SETTINGS_V2_API,
  PairedCostSettingsJSON,
  TKV2_ORDER_ATTACHMENT_API,
  TKV2_ORDER_PAIRED_COST_SETTINGS_API,
  Tkv2PairedCostSettings,
  expected,
} from '@aloft/toolkit-shared';

import { useEffect, useMemo, useState } from 'react';

import { useGetAllCompData } from '../../hooks/api/schema-hooks/useGetAllCompData.ts';
import { useGetMarketAdjustments } from '../../hooks/api/schema-hooks/useGetMarketAdjustments.ts';
import { useGetOrderDepreciationSettingsV2API } from '../../hooks/api/schema-hooks/useGetOrderDepreciationSettingsV2API.ts';
import { useGetSubjectData } from '../../hooks/api/schema-hooks/useGetSubjectData.ts';
import { useGetTkv2OrderAPI } from '../../hooks/api/schema-hooks/useGetTkv2OrderAPI.ts';
import {
  useGetTkv2MarketConditionsAPI,
  useGetTkv2OrderFeaturesAPI,
  useGetTkv2OrderPairedCostSettingsAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';
import { clearOrder } from '../../redux/features/actions/clearOrder.ts';
import {
  LoadPropertyPayload,
  loadProperties,
} from '../../redux/features/actions/loadProperties.ts';
import {
  loadAdjustmentSettings,
  loadDepreciationSettings,
  loadMarketAdjustments,
  loadMarketConditions,
  loadOrderData,
  loadOrderFeatures,
  setOrder,
} from '../../redux/features/orderSlice.ts';
import {
  selectShouldLoadAdjustmentSettings,
  selectShouldLoadComps,
  selectShouldLoadDepreciationSettings,
  selectShouldLoadMarketAdjustments,
  selectShouldLoadMarketConditions,
  selectShouldLoadOrderFeatures,
  selectShouldLoadSubject,
} from '../../redux/features/selectors/orderSelectors.ts';
import { setInitialOpinionOfSiteValue } from '../../redux/features/thunks/setInitialOpinionOfSiteValue.ts';
import { backfillDefaultFeatureAreas } from '../../redux/features/thunks/backfillDefaultFeatureAreas.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { useOrderSyncDriver } from './useOrderSyncDriver.ts';

type LoadStatus = 'loading' | 'error' | 'success';

const STALE_TIME_MS = 1000;

export const useOrderLoadSyncDriver = (orderID: string): LoadStatus => {
  const dispatch = useAppDispatch();

  const [propertiesLoadStatus, setPropertiesLoadStatus] = useState<LoadStatus>('loading');
  const [orderLoadStatus, setOrderLoadStatus] = useState<LoadStatus>('loading');
  const [marketAdjustmentsLoadStatus, setMarketAdjustmentsLoadStatus] =
    useState<LoadStatus>('loading');
  const [marketConditionsLoadStatus, setMarketConditionsLoadStatus] =
    useState<LoadStatus>('loading');
  const [adjustmentSettingsLoadStatus, setAdjustmentSettingsLoadStatus] =
    useState<LoadStatus>('loading');
  const [depreciationSettingsLoadStatus, setDepreciationSettingsLoadStatus] =
    useState<LoadStatus>('loading');

  const {
    status: orderQueryStatus,
    isStale: orderQueryIsStale,
    error: orderQueryError,
    data: orderData,
  } = useGetTkv2OrderAPI(
    {
      pathParams: { orderID },
    },
    { staleTime: STALE_TIME_MS, refetchOnWindowFocus: false },
  );

  // sync order details after load
  useOrderSyncDriver(orderID, orderData);

  const shouldLoadSubject = useAppSelector(selectShouldLoadSubject);
  const subjectQuery = useGetSubjectData(
    {
      pathParams: { orderID },
    },
    { enabled: shouldLoadSubject, staleTime: STALE_TIME_MS },
  );
  const {
    status: subjectQueryStatus,
    isStale: subjectQueryIsStale,
    error: subjectQueryError,
    data: subjectData,
  } = subjectQuery;

  const shouldLoadComps = useAppSelector(selectShouldLoadComps);
  const {
    status: compsQueryStatus,
    isStale: compsQueryIsStale,
    error: compsQueryError,
    data: compData,
  } = useGetAllCompData(
    {
      pathParams: { orderID },
    },
    { enabled: shouldLoadComps, staleTime: STALE_TIME_MS },
  );

  const shouldLoadOrderFeatures = useAppSelector(selectShouldLoadOrderFeatures);
  const {
    data: orderFeaturesData,
    isStale: orderFeaturesQueryIsStale,
    status: orderFeaturesQueryStatus,
    error: orderFeaturesQueryError,
  } = useGetTkv2OrderFeaturesAPI(
    {
      pathParams: { orderID },
    },
    { enabled: shouldLoadOrderFeatures, staleTime: STALE_TIME_MS },
  );

  const shouldLoadMarketAdjustments = useAppSelector(selectShouldLoadMarketAdjustments);
  const {
    status: marketAdjustmentsQueryStatus,
    isStale: marketAdjustmentsQueryIsStale,
    error: marketAdjustmentsQueryError,
    data: marketAdjustmentsData,
  } = useGetMarketAdjustments(
    {
      pathParams: { orderID },
    },
    { enabled: shouldLoadMarketAdjustments, staleTime: STALE_TIME_MS },
  );

  const shouldLoadConditionsData = useAppSelector(selectShouldLoadMarketConditions);
  const {
    status: marketConditionsQueryStatus,
    isStale: marketConditionsQueryIsStale,
    error: marketConditionsQueryError,
    data: marketConditionsData,
  } = useGetTkv2MarketConditionsAPI(
    {
      pathParams: { orderID },
    },
    { enabled: shouldLoadConditionsData, staleTime: STALE_TIME_MS },
  );

  const shouldLoadAdjustmentSettings = useAppSelector(selectShouldLoadAdjustmentSettings);
  const {
    status: adjustmentSettingsQueryStatus,
    isStale: adjustmentSettingsQueryIsStale,
    error: adjustmentSettingsQueryError,
    data: adjustmentSettingsData,
  } = useGetTkv2OrderPairedCostSettingsAPI(
    {
      pathParams: { orderID },
    },
    { enabled: shouldLoadAdjustmentSettings, staleTime: STALE_TIME_MS },
  );

  const shouldLoadDepreciationSettings = useAppSelector(selectShouldLoadDepreciationSettings);
  const {
    status: depreciationSettingsQueryStatus,
    isStale: depreciationSettingsQueryIsStale,
    error: depreciationSettingsQueryError,
    data: depreciationSettingsData,
  } = useGetOrderDepreciationSettingsV2API(
    {
      pathParams: { orderID },
    },
    {
      enabled: shouldLoadDepreciationSettings,
      staleTime: STALE_TIME_MS,
    },
  );

  // Order Change - clear out any old data, and set the order id
  useEffect(() => {
    dispatch(setOrder(orderID));
  }, [dispatch, orderID]);

  useEffect(
    () => () => {
      dispatch(clearOrder()); // make sure everything is cleared out of state from the order when we unload the module
    },
    [dispatch],
  );

  useEdgeTrigger({
    condition: orderQueryStatus !== 'pending' && !orderQueryIsStale,
    delayMs: 0,
    action: () => {
      if (orderQueryStatus === 'error') {
        console.error('Failed to load order', orderQueryError);
        setOrderLoadStatus('error');
        return;
      }

      const {
        input,
        isArchived = false,
        isDeleted = false,
        isTraining = false,
        isInspectifyOrder = false,
        clientName = '',
        inspectorType = '',
        borrowerName = '',
        borrowerPhone = '',
        borrowerEmail = '',
        appraisalType,
        inspectionDate,
        intendedUsers = '',
        intendedUse = '',
        appraiserInstructions = '',
        signedAt,
        isRevision = false,
        isMock = false,
        appraisifyFee,
        appraiserFee,
        startedAt,
        isEscalated = false,
        escalationType,
        propertyType,
        createdAt,
        isInternalValuation = false,
        qualityControlPassed = false,
        qualityControlIterations = 0,
      } = expected(orderData);

      const {
        craftsmanAdjustmentVersion,
        craftsmanCalculatorVersion,
        craftsmanDatasetVersion,
        effectiveDate,
        costMultiplier,
        orderNumber,
        licenseState,
        licenseNumber,
        licenseExpiration,
        licenseType,
        inspectionData,
        previousDesktopOrderID,
        revisionData,
        previousRevisionsData,
        inspectionId,
        revisionRequestId,
        options,
        appraisifyAddress,
        appraisifyName,
        appraisalReportDisclaimer,
        propertyType: inputPropertyType,
        salesComparisonApproach,
        clientLogoUrl,
      } = input;

      const finalPropertyType: MainPropertyType | undefined = propertyType ?? inputPropertyType;

      if (
        !['singleFamilyResidence', 'multiFamily', 'manufacturedHome', 'townhouse'].includes(
          finalPropertyType,
        )
      ) {
        throw new Error(`Invalid property type provided: ${finalPropertyType}`);
      }

      dispatch(
        loadOrderData({
          effectiveDate: effectiveDate.toJSON(),
          craftsmanAdjustmentVersion,
          craftsmanCalculatorVersion,
          craftsmanDatasetVersion,
          propertyType: finalPropertyType,
          costMultiplier,
          isArchived,
          isDeleted,
          isTraining,
          isInspectifyOrder,
          hasArvOption: options?.arv === true,
          hasAvmOption: options?.avm === true,
          // Add Rental Comparison and Income approach option check when implemented
          hasRentalConfiguration: options?.avm === true,
          clientName,
          inspectorType,
          borrowerName,
          borrowerPhone,
          borrowerEmail,
          appraisalType,
          inspectionDate: inspectionDate?.toJSON(),
          intendedUsers,
          intendedUse,
          appraiserInstructions,
          orderNumber,
          licenseState,
          licenseNumber,
          licenseExpiration: licenseExpiration?.toJSON(),
          licenseType,
          signedAt: signedAt?.toJSON(),
          inspectionData,
          previousDesktopOrderID,
          revisionData,
          previousRevisionsData,
          inspectionId,
          revisionRequestId,
          isRevision,
          appraisifyAddress,
          appraisifyName,
          isMock,
          appraiserFee: appraiserFee?.toJSON(),
          appraisifyFee: appraisifyFee?.toJSON(),
          startedAt: startedAt?.toJSON(),
          appraisalReportDisclaimer,
          isEscalated,
          escalationType,
          salesComparisonApproach: salesComparisonApproach ?? 'depreciated_cost',
          includeCostApproach: options?.includeCostApproach ?? true,
          createdAt: createdAt?.toJSON() ?? AloftTime.now().toJSON(),
          isInternalValuation,
          clientLogoUrl,
          qualityControlPassed,
          qualityControlIterations,
        }),
      );
      setOrderLoadStatus('success');
    },
  });

  useEdgeTrigger({
    condition:
      compsQueryStatus !== 'pending' &&
      !compsQueryIsStale &&
      subjectQueryStatus !== 'pending' &&
      !subjectQueryIsStale,
    delayMs: 0,
    action: () => {
      if (compsQueryStatus === 'error' || subjectQueryStatus === 'error') {
        console.error('Failed to load properties', compsQueryError, subjectQueryError);
        setPropertiesLoadStatus('error');
        return;
      }

      const { sequence, rentalSequence, comps } = expected(compData);
      const compIds = [...new Set([...sequence, ...rentalSequence])];

      const properties: LoadPropertyPayload[] = [{ id: 'subject', ...expected(subjectData) }];
      compIds.forEach((compId) => {
        properties.push({ id: compId, ...expected(comps[compId]) });
      });

      dispatch(
        loadProperties({
          properties,
          compSequence: sequence,
          rentalCompSequence: rentalSequence,
        }),
      );
      setPropertiesLoadStatus('success');
    },
  });

  useEdgeTrigger({
    condition: orderFeaturesQueryStatus !== 'pending' && !orderFeaturesQueryIsStale,
    delayMs: 0,
    action: () => {
      const parsed = TKV2_ORDER_ATTACHMENT_API.ORDER_FEATURES_API.GET.RESPONSE.safeParse(
        expected(orderFeaturesData),
      );

      if (orderFeaturesQueryStatus === 'error' && !parsed.success) {
        console.error('Failed to load order features', orderFeaturesQueryError);
        setOrderLoadStatus('error');
        return;
      }

      const orderFeatures = expected(parsed.data).body.features;
      dispatch(loadOrderFeatures(orderFeatures));
    },
  });

  useEdgeTrigger({
    condition: marketAdjustmentsQueryStatus !== 'pending' && !marketAdjustmentsQueryIsStale,
    delayMs: 0,
    action: () => {
      if (marketAdjustmentsQueryStatus === 'error') {
        console.error('Failed to load market adjustments', marketAdjustmentsQueryError);
        setMarketAdjustmentsLoadStatus('error');
        return;
      }

      const { body } = TKV2_ORDER_ATTACHMENT_API.MARKET_ADJUSTMENTS.GET.RESPONSE.parse(
        expected(marketAdjustmentsData),
      );
      dispatch(loadMarketAdjustments(body));
      setMarketAdjustmentsLoadStatus('success');
    },
  });

  useEdgeTrigger({
    condition: marketConditionsQueryStatus !== 'pending' && !marketConditionsQueryIsStale,
    delayMs: 0,
    action: () => {
      if (marketConditionsQueryStatus === 'error') {
        console.error('Failed to load market conditions', marketConditionsQueryError);
        setMarketConditionsLoadStatus('error');
        return;
      }

      const { body } = TKV2_ORDER_ATTACHMENT_API.MARKET_CONDITIONS.GET.RESPONSE.parse(
        expected(marketConditionsData),
      );
      dispatch(loadMarketConditions(body));
      setMarketConditionsLoadStatus('success');
    },
  });

  useEdgeTrigger({
    condition: adjustmentSettingsQueryStatus !== 'pending' && !adjustmentSettingsQueryIsStale,
    delayMs: 0,
    action: () => {
      if (adjustmentSettingsQueryStatus === 'error') {
        console.error('Failed to load adjustment settings', adjustmentSettingsQueryError);
        setAdjustmentSettingsLoadStatus('error');
        return;
      }

      const settings = convertSettingsToJSON(
        TKV2_ORDER_PAIRED_COST_SETTINGS_API.GET.RESPONSE.parse(adjustmentSettingsData),
      );

      dispatch(loadAdjustmentSettings(settings));
      setAdjustmentSettingsLoadStatus('success');
    },
  });

  useEdgeTrigger({
    condition: depreciationSettingsQueryStatus !== 'pending' && !depreciationSettingsQueryIsStale,
    delayMs: 0,
    action: () => {
      if (depreciationSettingsQueryStatus === 'error') {
        console.error('Failed to load depreciation settings', depreciationSettingsQueryError);
        setDepreciationSettingsLoadStatus('error');
        return;
      }
      const settings = ORDER_DEPRECIATION_SETTINGS_V2_API.GET.RESPONSE.parse(
        expected(depreciationSettingsData),
      );

      dispatch(loadDepreciationSettings(settings));
      setDepreciationSettingsLoadStatus('success');
    },
  });

  const overallStatus = useMemo(() => {
    if (
      orderLoadStatus === 'error' ||
      propertiesLoadStatus === 'error' ||
      marketAdjustmentsLoadStatus === 'error' ||
      marketConditionsLoadStatus === 'error' ||
      adjustmentSettingsLoadStatus === 'error' ||
      depreciationSettingsLoadStatus === 'error'
    ) {
      return 'error';
    }

    if (
      orderLoadStatus === 'loading' ||
      propertiesLoadStatus === 'loading' ||
      marketAdjustmentsLoadStatus === 'loading' ||
      marketConditionsLoadStatus === 'loading' ||
      adjustmentSettingsLoadStatus === 'loading' ||
      depreciationSettingsLoadStatus === 'loading'
    ) {
      return 'loading';
    }

    return 'success';
  }, [
    adjustmentSettingsLoadStatus,
    orderLoadStatus,
    marketAdjustmentsLoadStatus,
    marketConditionsLoadStatus,
    propertiesLoadStatus,
    depreciationSettingsLoadStatus,
  ]);

  // Once all data is loaded, we can calculate subject's initial site value
  useEdgeTrigger({
    condition: overallStatus === 'success',
    delayMs: 0,
    action: () => dispatch(setInitialOpinionOfSiteValue()),
  });

  useEdgeTrigger({
    condition: overallStatus === 'success',
    delayMs: 0,
    action: () => dispatch(backfillDefaultFeatureAreas()),
  });

  return overallStatus;
};

const convertSettingsToJSON = (settings: Tkv2PairedCostSettings): PairedCostSettingsJSON => ({
  ...settings,
  rounding: Object.fromEntries(
    Object.entries(settings.rounding).map(([key, val]) => [key, val?.toJSON()]),
  ),
  threshold: Object.fromEntries(
    Object.entries(settings.threshold).map(([key, val]) => [key, val?.toJSON()]),
  ),
  roundingDefault: settings.roundingDefault.toJSON(),
  thresholdDefault: settings.thresholdDefault.toJSON(),
});
